import { useEffect, useRef } from "react";

const W = 480;
const H = 580;
const MAX_DEPTH = 6;

/* deep → light greens, plus a gold accent (matches the page-wide falling leaves) */
const LEAF_PALETTE = [
  ["#3F6B3A", "#2E5230"],
  ["#4E7A42", "#3A6136"],
  ["#5C8A4E", "#46703E"],
  ["#6E9A57", "#54824A"],
  ["#8FAE5D", "#6E9A57"],
  ["#A9CB7E", "#7FA85E"],
  ["#C8A24A", "#96762C"],
];

const BARK = ["#4E3B28", "#57422E", "#604A34", "#684F37"];

/* A realistic procedural tree:
   - tapered, gently curved trunk and limbs (filled polygons, not lines)
   - upward-biased natural branching
   - ~2,600 individual leaf sprites with depth shading (dark core, lit edges)
   - the whole tree sways in an ambient breeze; scrolling adds gusts
   - leaves occasionally detach from the canopy and flutter to the ground */
export const RealisticTree = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    let seed = 20260612;
    const rng = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    /* pre-rendered leaf sprites */
    const sprites = LEAF_PALETTE.map(([c1, c2]) => {
      const s = document.createElement("canvas");
      s.width = 24;
      s.height = 24;
      const sc = s.getContext("2d");
      sc.translate(12, 12);
      const grad = sc.createLinearGradient(0, -9, 0, 9);
      grad.addColorStop(0, c1);
      grad.addColorStop(1, c2);
      sc.fillStyle = grad;
      sc.beginPath();
      sc.moveTo(0, -9);
      sc.bezierCurveTo(7, -5, 6, 5, 0, 9);
      sc.bezierCurveTo(-6, 5, -7, -5, 0, -9);
      sc.fill();
      sc.strokeStyle = c2;
      sc.globalAlpha = 0.55;
      sc.lineWidth = 0.6;
      sc.beginPath();
      sc.moveTo(0, -8);
      sc.lineTo(0, 8);
      sc.stroke();
      return s;
    });

    const pickCi = () => {
      const r = rng();
      if (r < 0.07) return 6; // gold accent
      if (r < 0.22) return rng() < 0.5 ? 0 : 1; // deep shade (inner canopy)
      if (r < 0.74) return 2 + ((rng() * 2) | 0); // mid greens
      return 4 + ((rng() * 2) | 0); // sunlit edges
    };

    /* ---- build the skeleton (parents precede children) ---- */
    const branches = [];
    const terminals = [];
    const build = (parent, len, width, angle, depth) => {
      const b = {
        parent,
        len,
        width,
        angle,
        depth,
        bend: (rng() - 0.5) * 0.35,
        phase: rng() * 6.28,
        leaves: null,
        x: 0, y: 0, ex: 0, ey: 0, abs: 0,
      };
      const id = branches.length;
      branches.push(b);
      const isTerminal = depth >= MAX_DEPTH || width < 0.7;

      if (depth >= 4) {
        const n = isTerminal ? 10 + ((rng() * 7) | 0) : 3 + ((rng() * 3) | 0);
        b.leaves = [];
        for (let i = 0; i < n; i++) {
          const a = rng() * 6.28;
          const d = Math.sqrt(rng()) * (isTerminal ? 21 : 12);
          b.leaves.push({
            ox: Math.cos(a) * d,
            oy: Math.sin(a) * d * 0.85,
            size: 3.2 + rng() * 4.2,
            ci: pickCi(),
            phase: rng() * 6.28,
            rot0: rng() * 6.28,
          });
        }
      }
      if (isTerminal) {
        terminals.push(id);
        return;
      }
      const kids = depth === 0 ? 3 : depth < 3 ? (rng() > 0.35 ? 3 : 2) : 2;
      for (let i = 0; i < kids; i++) {
        const spread =
          (kids === 1 ? 0 : (i / (kids - 1) - 0.5)) * (0.75 + rng() * 0.45) +
          (rng() - 0.5) * 0.22;
        build(id, len * (0.64 + rng() * 0.15), width * (0.56 + rng() * 0.1), spread * 0.92, depth + 1);
      }
    };
    build(null, 128, 25, -Math.PI / 2 + 0.05, 0);

    /* ---- wind ---- */
    let lastScroll = window.scrollY;
    let gust = 0;
    const onScroll = () => {
      const d = window.scrollY - lastScroll;
      lastScroll = window.scrollY;
      gust += d * 0.0008;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    /* tapered limb with a gentle curve */
    const drawTapered = (b) => {
      const dx = Math.cos(b.abs);
      const dy = Math.sin(b.abs);
      const nx = -dy;
      const ny = dx;
      const mx = b.x + dx * b.len * 0.5 + nx * b.len * b.bend * 0.15;
      const my = b.y + dy * b.len * 0.5 + ny * b.len * b.bend * 0.15;
      const w0 = b.width / 2;
      const w1 = Math.max((b.width * 0.62) / 2, 0.5);
      const wm = (w0 + w1) * 0.55;
      ctx.beginPath();
      ctx.moveTo(b.x + nx * w0, b.y + ny * w0);
      ctx.quadraticCurveTo(mx + nx * wm, my + ny * wm, b.ex + nx * w1, b.ey + ny * w1);
      ctx.lineTo(b.ex - nx * w1, b.ey - ny * w1);
      ctx.quadraticCurveTo(mx - nx * wm, my - ny * wm, b.x - nx * w0, b.y - ny * w0);
      ctx.closePath();
      ctx.fill();
    };

    /* leaves detaching from the canopy */
    let fallers = [];
    let lastSpawn = 0;
    const spawnFaller = () => {
      const b = branches[terminals[(Math.random() * terminals.length) | 0]];
      fallers.push({
        x: b.ex + (Math.random() - 0.5) * 16,
        y: b.ey + (Math.random() - 0.5) * 12,
        vx: -0.25 + Math.random() * 0.5,
        vy: 0.35 + Math.random() * 0.5,
        angle: Math.random() * 6.28,
        spin: (-0.5 + Math.random()) * 0.03,
        phase: Math.random() * 6.28,
        flutter: 0.03 + Math.random() * 0.03,
        size: 3.5 + Math.random() * 3.5,
        ci: pickCi(),
        t: 0,
      });
    };

    let raf;
    const tick = (t) => {
      ctx.clearRect(0, 0, W, H);
      gust *= 0.95;
      const wind =
        Math.sin(t * 0.00045) * 0.55 +
        Math.sin(t * 0.0012 + 1.7) * 0.3 +
        Math.max(-1.6, Math.min(1.6, gust * 40));

      /* ground shadow */
      const gs = ctx.createRadialGradient(W / 2, 556, 8, W / 2, 556, 120);
      gs.addColorStop(0, "rgba(18,42,29,0.16)");
      gs.addColorStop(1, "rgba(18,42,29,0)");
      ctx.fillStyle = gs;
      ctx.save();
      ctx.translate(W / 2, 556);
      ctx.scale(1, 0.13);
      ctx.translate(-W / 2, -556);
      ctx.beginPath();
      ctx.arc(W / 2, 556, 120, 0, 6.29);
      ctx.fill();
      ctx.restore();

      /* pass 1: skeleton */
      ctx.lineCap = "round";
      for (let i = 0; i < branches.length; i++) {
        const b = branches[i];
        let baseX, baseY, parentAbs;
        if (b.parent === null) {
          baseX = W / 2;
          baseY = 552;
          parentAbs = 0;
        } else {
          const p = branches[b.parent];
          baseX = p.ex;
          baseY = p.ey;
          parentAbs = p.abs;
        }
        const flex = Math.pow(b.depth / MAX_DEPTH, 2);
        const sway = wind * 0.05 * flex + Math.sin(t * 0.001 + b.phase) * 0.014 * flex;
        b.abs = parentAbs + b.angle + sway;
        b.x = baseX;
        b.y = baseY;
        b.ex = baseX + Math.cos(b.abs) * b.len;
        b.ey = baseY + Math.sin(b.abs) * b.len;

        if (b.depth <= 3) {
          ctx.fillStyle = BARK[b.depth];
          drawTapered(b);
        } else {
          ctx.strokeStyle = b.depth === 4 ? "#6B5138" : "#75603F";
          ctx.lineWidth = Math.max(b.width, 0.6);
          const dx = Math.cos(b.abs);
          const dy = Math.sin(b.abs);
          const mx = b.x + dx * b.len * 0.5 - dy * b.len * b.bend * 0.15;
          const my = b.y + dy * b.len * 0.5 + dx * b.len * b.bend * 0.15;
          ctx.beginPath();
          ctx.moveTo(b.x, b.y);
          ctx.quadraticCurveTo(mx, my, b.ex, b.ey);
          ctx.stroke();
        }
      }

      /* pass 2: foliage */
      for (let i = 0; i < branches.length; i++) {
        const b = branches[i];
        if (!b.leaves) continue;
        const cosA = Math.cos(b.abs);
        const sinA = Math.sin(b.abs);
        for (const lf of b.leaves) {
          const px = b.ex + lf.ox * cosA - lf.oy * sinA;
          const py = b.ey + lf.ox * sinA + lf.oy * cosA;
          const rot =
            b.abs + lf.rot0 + Math.sin(t * 0.0018 + lf.phase) * (0.18 + Math.abs(wind) * 0.12);
          const s = lf.size / 9;
          const cs = Math.cos(rot) * s;
          const sn = Math.sin(rot) * s;
          ctx.setTransform(dpr * cs, dpr * sn, -dpr * sn, dpr * cs, dpr * px, dpr * py);
          ctx.drawImage(sprites[lf.ci], -12, -12);
        }
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      /* pass 3: leaves shed from the canopy */
      if (t - lastSpawn > 1100 - Math.min(800, Math.abs(gust) * 9000) && fallers.length < 9) {
        lastSpawn = t;
        spawnFaller();
      }
      const g = Math.max(-7, Math.min(7, gust * 90));
      fallers = fallers.filter((f) => f.y < H + 20 && f.x > -30 && f.x < W + 30);
      for (const f of fallers) {
        f.t++;
        f.x += f.vx + g * 0.4 + Math.sin(f.t * 0.02) * 0.5;
        f.y += f.vy + Math.abs(g) * 0.15;
        f.angle += f.spin + g * 0.003;
        f.phase += f.flutter;
        const flip = 0.35 + 0.65 * Math.abs(Math.sin(f.phase));
        const s = f.size / 9;
        const cs = Math.cos(f.angle) * s;
        const sn = Math.sin(f.angle) * s;
        ctx.setTransform(dpr * cs, dpr * sn, -dpr * sn, dpr * cs, dpr * f.x, dpr * f.y);
        ctx.transform(1, 0, 0, flip, 0, 0);
        ctx.globalAlpha = Math.min(1, (H - f.y) / 60 + 0.2);
        ctx.drawImage(sprites[f.ci], -12, -12);
        ctx.globalAlpha = 1;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div
      data-testid="hero-tree-scene"
      className="relative w-[440px] h-[540px] lg:w-[480px] lg:h-[580px]"
      aria-hidden="true"
    >
      <canvas ref={ref} data-testid="realistic-tree-canvas" style={{ width: "100%", height: "100%" }} />
    </div>
  );
};
